import { useEffect, useRef } from "react";
import { scaleSequential, interpolateMagma, interpolateViridis } from "d3";
import { CELL_COUNT, GRID_COLS, GRID_ROWS, CommonsCell, HeatCell } from "../types";
import { normalizeHeatValue } from "../lib/attention";

interface Props {
  heat?: HeatCell[];
  commons?: CommonsCell[];
  blend?: boolean;
  label?: string;
}

export function HeatCanvas({ heat = [], commons = [], blend = true, label = "attention heatmap" }: Props) {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const width = parent.clientWidth || 960;
    const height = parent.clientHeight || 640;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const localByIndex = new Map(heat.map((cell) => [cell.i, cell]));
    const commonsByIndex = new Map(commons.map((cell) => [cell.i, cell]));
    const localScale = scaleSequential(interpolateMagma).domain([0, 1]);
    const commonsScale = scaleSequential(interpolateViridis).domain([0, 1]);
    const cellW = width / GRID_COLS;
    const cellH = height / GRID_ROWS;

    for (let i = 0; i < CELL_COUNT; i += 1) {
      const x = (i % GRID_COLS) * cellW;
      const y = Math.floor(i / GRID_COLS) * cellH;
      const localValue = normalizeHeatValue(localByIndex.get(i) || { dwellMs: 0, touches: 0, visits: 0 }, 5200);
      const commonsValue = normalizeHeatValue(commonsByIndex.get(i) || { dwellMs: 0, touches: 0, visits: 0 }, 30_000);
      if (blend && commonsValue > 0.01) {
        ctx.globalAlpha = 0.18 + commonsValue * 0.32;
        ctx.fillStyle = commonsScale(commonsValue);
        ctx.fillRect(x + 2, y + 2, cellW - 4, cellH - 4);
      }
      if (localValue > 0.01) {
        ctx.globalAlpha = 0.25 + localValue * 0.65;
        ctx.fillStyle = localScale(localValue);
        const radius = Math.min(cellW, cellH) * (0.22 + localValue * 0.28);
        const gradient = ctx.createRadialGradient(x + cellW / 2, y + cellH / 2, 0, x + cellW / 2, y + cellH / 2, radius * 1.9);
        gradient.addColorStop(0, localScale(localValue));
        gradient.addColorStop(1, "rgba(8,7,17,0)");
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x + cellW / 2, y + cellH / 2, radius * 1.9, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  }, [heat, commons, blend]);

  return <canvas ref={ref} className="heat-canvas" aria-label={label} />;
}
